import { createInstance, dateReviver } from '@/api';
import {
    SourceSpecificationClient,
    SourceSpecificationCreateDto,
    SourceSpecificationDetailDto,
    SourceSpecificationUpdateDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';

export interface SourceSpecificationStoreState {
    items?: { key: string; value: SourceSpecificationDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new SourceSpecificationClient('', createInstance(), dateReviver);

export const useSourceSpecificationStore = defineStore('configuration-source-specification-store', {
    state: (): SourceSpecificationStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): SourceSpecificationDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async getByDefinition(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${id}`;

            try {
                const response = await client.getByDefinition(id);
                if (response.length) {
                    this.items = [{ key: key, value: response }];
                } else {
                    this.items = [];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('sourcespecification.detail.get.error.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(definitionId: number, dto: SourceSpecificationCreateDto) {
            let hasError = false;
            this.reloading = true;

            try {
                const response = await client.create(dto);

                await this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('sourcespecification.detail.error.create.title'), error);
                return null;
            } finally {
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeCreate();
                }
                this.reloading = false;
            }
        },
        async update(definitionId: number, id: number, dto: SourceSpecificationUpdateDto) {
            let hasError = false;
            this.reloading = true;

            try {
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('sourcespecification.detail.success.update.title'));
                await this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('sourcespecification.detail.error.update.title'), error);
            } finally {
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeEdit();
                }
                this.reloading = false;
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;

            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('sourcespecification.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('sourcespecification.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(id: number) {
            const key = `definition-${id}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
